import { WindowRule, WindowType } from "@gymflow/types";
import { AxiosError } from "axios";
import { startCase } from "lodash";
import { DateTime, Duration } from "luxon";

const isISODateOnly = (input: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) return false;
  return DateTime.fromISO(input, { setZone: true }).isValid;
};
export const windowValueToTimeUnitMap: { [key in WindowType]: keyof Duration } =
  {
    DAYS: "days",
    MONTHS: "months",
    HOURS: "hours",
    MINUTES: "minutes",
  };
const durationFromWindow = (rule: WindowRule) => {
  return Duration.fromObject({
    [windowValueToTimeUnitMap[rule.windowType]]: rule.windowValue,
  });
};
const parseErrorCode = (error: AxiosError<any, any>) => {
  const interpolationMap = Object.entries(
    (error.response?.data.error_details ?? {}) as Record<string, string>,
  ).reduce(
    (acc, [key, value]) => {
      if (Duration.fromISO(value).isValid) {
        acc[key] = Duration.fromISO(value).rescale().toHuman({
          unitDisplay: "long",
          listStyle: "long",
        });
      } else if (DateTime.fromISO(value).isValid) {
        acc[key] = DateTime.fromISO(value).toLocaleString(
          isISODateOnly(value)
            ? DateTime.DATE_MED_WITH_WEEKDAY
            : DateTime.DATETIME_MED,
        );
      } else if (key === "windowType") {
        /*
          This code path handles windows manually, in the future windows should
          use the ISO Duration standard instead and would be handled by the code above
        */
        acc[key] = value;
        acc["window"] = durationFromWindow({
          windowType: value as WindowType,
          windowValue: "1",
        })
          .rescale()
          .toHuman({
            unitDisplay: "long",
            listStyle: "long",
          });
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
  return {
    errorCode: error.response?.data.error_code,
    fallbackMessage: error.response?.data.error_message,
    interpolationMap,
  };
};

/*** @deprecated
 * this exists to support the old error format, there are no types
 * for this cause it is a copy paste from js land
 */
const parseErrorDeprecated = (errors: Record<string, any>) => {
  const { global: _, error_code, ...rest } = errors;

  const fieldErrors = Object.entries(rest).reduce(
    (acc, [key, value]) => {
      if (key !== "error_message" && value === (errors as any).error_message)
        return acc;

      acc[key] = value;
      return acc;
    },
    {} as Record<string, any>,
  );
  const global = errors["global"];

  let text = "";

  const fieldErrorString = Object.keys(fieldErrors).reduce(
    (innerText, field) => {
      if (field === "id" || field.endsWith("Id")) {
        innerText += fieldErrors[field];
      } else {
        innerText += startCase(field) + ": " + fieldErrors[field];
      }
      return innerText + "\n";
    },
    "",
  );
  if (typeof global !== "undefined") {
    text = `${global}\n`;
  }
  text += fieldErrorString;

  return {
    errorCode: undefined,
    fallbackMessage: text,
    interpolationMap: {},
  };
};

export const backendErrorsHelper = {
  parseError: (error: AxiosError<any, any>) => {
    if ("error_code" in error.response?.data) {
      return parseErrorCode(error);
    } else {
      return parseErrorDeprecated(error.response?.data);
    }
  },
};
